import {
  Button,
  cn,
  type PaginationState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  WithToolTip,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IIdpGroup, IUserInfo } from "@models";
import {
  AddIdpGroupDialog,
  AddUserDialog,
  ApplicationGroupMapping,
  Applications,
  IdpGroups,
  Summary,
  Users,
  useAdministerUsers,
} from "@modules";
import { useCompany, useCompanyMetrics, useUserInfo } from "@query";
import { APP_BASE_PATH } from "@shared";
import { cva } from "class-variance-authority";
import { Info, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

function isSamlAuthType(authType?: string): boolean {
  return authType?.toLowerCase() === "saml";
}

const SAML_ONLY_ADMINISTER_TABS = new Set([
  "idp-groups",
  "application-group-mapping",
]);

const administerPageLayoutVariants = cva(
  "flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-y-contain",
);

const administerTabsStickyBarVariants = cva(
  "sticky top-0 z-20 flex w-full shrink-0 items-center justify-between bg-background pb-2 pt-1",
);

const administerTabsContentVariants = cva(
  "mt-6 flex min-h-[min(500px,70vh)] flex-col overflow-hidden sm:min-h-[60vh]",
);

const administerMappingTabContentVariants = cva(
  "mt-6 flex flex-col pr-5 scrollbar-gutter-stable",
);

export function AdministerPage() {
  const { t } = useTranslation();
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const companyUuid = userInfo?.companyUuid;
  const { data: company } = useCompany(companyUuid, APP_BASE_PATH);
  const { data: metrics } = useCompanyMetrics(companyUuid, APP_BASE_PATH);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [emailSearch, setEmailSearch] = useState<string>("");
  const { users, rawUsers, totalElements, totalPages, isLoading } =
    useAdministerUsers(companyUuid, {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      emailSearch,
    });
  const [activeTab, setActiveTab] = useState("users");

  const isSamlAuth = useMemo(
    () => isSamlAuthType(company?.authType),
    [company?.authType],
  );

  useEffect(() => {
    if (isSamlAuth || !SAML_ONLY_ADMINISTER_TABS.has(activeTab)) {
      return;
    }
    setActiveTab("users");
  }, [activeTab, isSamlAuth]);

  const handleAdministerTabChange = useCallback(
    (nextTab: string) => {
      if (nextTab === activeTab) return;
      setActiveTab(nextTab);
    },
    [activeTab],
  );

  const handlePaginationChange = useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState),
    ) => {
      setPagination((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    [],
  );
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<IUserInfo | null>(null);
  const [addIdpGroupDialogOpen, setAddIdpGroupDialogOpen] = useState(false);
  const [editIdpGroup, setEditIdpGroup] = useState<IIdpGroup | null>(null);

  const handleOpenAddUser = () => {
    setEditUser(null);
    setAddUserDialogOpen(true);
  };

  const handleOpenEditUser = useCallback(
    (user: { id: string }) => {
      const raw = rawUsers.find((u) => u.id === user.id);
      if (raw) {
        setEditUser(raw);
        setAddUserDialogOpen(true);
      }
    },
    [rawUsers],
  );

  const handleCloseUserDialog = (open: boolean) => {
    setAddUserDialogOpen(open);
    if (!open) setEditUser(null);
  };

  const handleOpenAddIdpGroup = () => {
    setEditIdpGroup(null);
    setAddIdpGroupDialogOpen(true);
  };

  const handleOpenEditIdpGroup = useCallback((group: IIdpGroup) => {
    setEditIdpGroup(group);
    setAddIdpGroupDialogOpen(true);
  }, []);

  const handleCloseIdpGroupDialog = (open: boolean) => {
    setAddIdpGroupDialogOpen(open);
    if (!open) setEditIdpGroup(null);
  };

  return (
    <div className={cn(administerPageLayoutVariants(), "h-full")}>
      <div className={cn("shrink-0 space-y-6")}>
        <Summary
          company={company}
          companyUuid={companyUuid}
          metrics={metrics}
          inactiveUsersCount={metrics?.inactiveUsersCount}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleAdministerTabChange}
        className="flex w-full flex-col">
        <div className={administerTabsStickyBarVariants()}>
          <TabsList variant="line" size="md" className="justify-start">
            <TabsTrigger value="users" className="gap-2">
              {t("hub.administer.users")}
              <WithToolTip
                className="max-w-80"
                tooltipText={t("hub.administer.users_tooltip", {
                  defaultValue:
                    "View and manage all your users. You can add and edit users and toggle account access. Use the search to quickly find users by name, email, or role.",
                })}>
                <div className="flex items-center justify-center">
                  <Info className="size-4 text-muted-foreground" />
                </div>
              </WithToolTip>
            </TabsTrigger>
            <TabsTrigger value="applications" className="gap-2">
              {t("hub.administer.applications")}
              <WithToolTip
                className="max-w-80"
                tooltipText={t("hub.administer.applications_tooltip", {
                  defaultValue:
                    "Monitor your applications. Review the distribution of your applications across cloud regions, verify cluster and tenant details, and ensure all environments are running the latest version.",
                })}>
                <div className="flex items-center justify-center">
                  <Info className="size-4 text-muted-foreground" />
                </div>
              </WithToolTip>
            </TabsTrigger>
            {isSamlAuth && (
              <TabsTrigger value="idp-groups" className="gap-2">
                {t("hub.administer.idp_groups")}
                <WithToolTip
                  className="max-w-80"
                  tooltipText={t("hub.administer.idp_groups_tooltip", {
                    defaultValue:
                      "Create and manage IDP groups for your company. Groups can be mapped to application roles on the Application Group Mapping tab.",
                  })}>
                  <div className="flex items-center justify-center">
                    <Info className="size-4 text-muted-foreground" />
                  </div>
                </WithToolTip>
              </TabsTrigger>
            )}
            {isSamlAuth && (
              <TabsTrigger value="application-group-mapping" className="gap-2">
                {t("hub.administer.application_group_mapping")}
                <WithToolTip
                  className="max-w-80"
                  tooltipText={t(
                    "hub.administer.application_group_mapping_tooltip",
                    {
                      defaultValue:
                        "Map application groups to IDP groups for each application. Drag groups to the center panel or select them to create mappings.",
                    },
                  )}>
                  <div className="flex items-center justify-center">
                    <Info className="size-4 text-muted-foreground" />
                  </div>
                </WithToolTip>
              </TabsTrigger>
            )}
          </TabsList>
          {activeTab === "users" && (
            <Button
              size="sm"
              className="shrink-0 gap-2"
              onClick={handleOpenAddUser}>
              <Plus className="size-4" />
              {t("hub.sidebar.user")}
            </Button>
          )}
          {isSamlAuth && activeTab === "idp-groups" && (
            <Button
              size="sm"
              className="shrink-0 gap-2"
              onClick={handleOpenAddIdpGroup}>
              <Plus className="size-4" />
              {t("hub.administer.add_idp_group")}
            </Button>
          )}
        </div>

        <TabsContent value="users" className={administerTabsContentVariants()}>
          <Users
            users={users}
            companyUuid={companyUuid}
            onEditUser={handleOpenEditUser}
            totalElements={totalElements}
            totalPages={totalPages}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            onSearchChange={setEmailSearch}
            loading={isLoading}
            loadingMessage={t("hub.administer.loading")}
          />
        </TabsContent>

        <TabsContent
          value="applications"
          className={administerTabsContentVariants()}>
          <Applications companyUuid={companyUuid} />
        </TabsContent>

        {isSamlAuth && (
          <TabsContent
            value="idp-groups"
            className={administerTabsContentVariants()}>
            <IdpGroups
              companyUuid={companyUuid}
              onEditGroup={handleOpenEditIdpGroup}
            />
          </TabsContent>
        )}

        {isSamlAuth && (
          <TabsContent
            value="application-group-mapping"
            className={administerMappingTabContentVariants()}>
            <ApplicationGroupMapping
              companyUuid={companyUuid}
              companyName={company?.name}
            />
          </TabsContent>
        )}
      </Tabs>

      <AddUserDialog
        open={addUserDialogOpen}
        onOpenChange={handleCloseUserDialog}
        companyUuid={companyUuid}
        user={editUser}
      />

      <AddIdpGroupDialog
        open={addIdpGroupDialogOpen}
        onOpenChange={handleCloseIdpGroupDialog}
        companyUuid={companyUuid}
        group={editIdpGroup}
      />
    </div>
  );
}
